'use client';

import * as React from 'react';
import { Check, ChevronDown, Loader2, X } from 'lucide-react';
import {
  getOfferPlanPrice,
  type ExclusiveOffer,
  type PlanBillingInterval,
  type PlanDefinition,
  type UpgradePlanName,
} from '@workspace/global-utils';
import { Button } from '@workspace/ui/components/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@workspace/ui/components/dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@workspace/ui/components/command';
import { Popover, PopoverContent, PopoverTrigger } from '@workspace/ui/components/popover';
import { Skeleton } from '@workspace/ui/components/skeleton';
import { ExclusiveOfferBanner } from '@workspace/ui/components/exclusive-offer-banner';
import { OfferDiscountChip } from '@workspace/ui/components/upgrade-offer';
import { useOfferCountdown } from '@workspace/ui/hooks/use-offer-countdown';
import { formatPlanPrice, getCurrencySymbol, PLAN_RANK } from '@workspace/ui/lib/upgrade-offer';
import { cn } from '@workspace/ui/lib/utils';

interface PlansModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // PLAN_DEFINITIONS from @workspace/global-utils, with prices in the selected currency.
  // The `recommended` plan is marked "Most popular" and shown first on phones.
  plans: PlanDefinition[];
  // Shows placeholders in place of prices while another currency loads.
  pricesLoading?: boolean;
  // Paid plan the account is on; leave out for Free.
  currentPlan?: UpgradePlanName;
  currentPlanLabel: string;
  defaultInterval?: PlanBillingInterval;
  annualSavingsPercent?: number;
  currency?: string;
  currencies?: readonly string[];
  onCurrencyChange?: (currency: string) => void;
  offer?: ExclusiveOffer | null;
  onTalkToFounder?: () => void;
  onSelectPlan: (plan: UpgradePlanName, interval: PlanBillingInterval) => void;
  // Plan whose checkout is opening; its button shows a spinner and the others lock.
  pendingPlan?: UpgradePlanName | null;
  onCompareFeatures: () => void;
  dataPhSelector: string;
}

const INTERVALS: { value: PlanBillingInterval; label: string }[] = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'annual', label: 'Annually' },
];

function CurrencyLabel({ currency }: { currency: string }) {
  return (
    <>
      {currency} <span className="max-sm:hidden">—</span> {getCurrencySymbol(currency)}
    </>
  );
}

const currencyNames =
  typeof Intl.DisplayNames !== 'undefined'
    ? new Intl.DisplayNames(['en'], { type: 'currency' })
    : null;

const currencyOptionLabel = (code: string) => `${code} - ${currencyNames?.of(code) ?? code}`;

function CurrencyPicker({
  currency,
  currencies,
  onChange,
  dataPhSelector,
}: {
  currency: string;
  currencies: readonly string[];
  onChange: (currency: string) => void;
  dataPhSelector: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const query = search.trim().toLowerCase();
  const options = currencies.filter(
    (code) => !query || currencyOptionLabel(code).toLowerCase().includes(query),
  );

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch('');
      }}
    >
      <PopoverTrigger
        className="flex shrink-0 cursor-pointer items-center gap-1.5 text-sm text-muted-foreground transition-[color] duration-150 hover:text-primary-darker"
        data-ph-selector={`${dataPhSelector}-currency`}
      >
        <CurrencyLabel currency={currency} />
        <ChevronDown
          className={cn('size-3 transition-transform duration-150', open && 'rotate-180')}
          aria-hidden="true"
        />
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={4} className="w-60 overflow-hidden p-0">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search currency..."
            value={search}
            onValueChange={setSearch}
            className="h-9 text-sm"
          />
          <CommandList className="max-h-56">
            <CommandEmpty>No currency found.</CommandEmpty>
            <CommandGroup>
              {options.map((code) => (
                <CommandItem
                  key={code}
                  value={code}
                  onSelect={() => {
                    onChange(code);
                    setOpen(false);
                    setSearch('');
                  }}
                  className="flex cursor-pointer items-center justify-between px-3 py-2 text-sm"
                >
                  <span>{currencyOptionLabel(code)}</span>
                  <Check
                    className={cn(
                      'size-3.5 shrink-0 text-primary',
                      code === currency ? 'opacity-100' : 'opacity-0',
                    )}
                    aria-hidden="true"
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function PlanCard({
  plan,
  interval,
  currency,
  pricesLoading,
  offer,
  currentPlan,
  hiddenOnMobile,
  onSelect,
  pending,
  locked,
  dataPhSelector,
}: {
  plan: PlanDefinition;
  interval: PlanBillingInterval;
  currency: string;
  pricesLoading: boolean;
  offer?: ExclusiveOffer;
  currentPlan?: UpgradePlanName;
  hiddenOnMobile: boolean;
  onSelect: () => void;
  // This card's checkout is opening.
  pending: boolean;
  // Another card's checkout is opening.
  locked: boolean;
  dataPhSelector: string;
}) {
  const { name, description, keyFeatures, recommended } = plan;
  const price = plan.pricing[interval];
  // Each card keeps its own currency: Dodo can price some plans and not others.
  const priceCurrency = price.currency ?? currency;
  const discount = offer?.discountPercent ?? 0;
  const { struck, monthly, annualTotal } = getOfferPlanPrice(price, interval, discount);
  // Without an offer the card shows the base price, less 15% on annual, and strikes nothing.
  const struckPrice = offer ? struck : null;
  const isOwned = currentPlan !== undefined && PLAN_RANK[name] <= PLAN_RANK[currentPlan];

  return (
    <div
      className={cn(
        'flex min-w-0 flex-col gap-4 rounded-[14px] bg-white p-4',
        recommended
          ? cn(
              'border-2 border-primary',
              offer ? 'shadow-plan-highlight-offer' : 'shadow-plan-highlight',
            )
          : 'border border-border',
        hiddenOnMobile && 'max-md:hidden',
      )}
    >
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-[22px] font-bold leading-[1.45] text-primary-darker md:text-xl md:leading-[1.3]">
            {name}
          </h3>
          {recommended && (
            <span className="shrink-0 rounded-[10px] bg-primary-light px-2.5 py-1 text-[11.5px] font-bold text-primary">
              Most popular
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>

      {pricesLoading ? (
        <div className="flex flex-col gap-2" aria-label="Loading prices">
          <Skeleton className="h-12 w-32" />
          <Skeleton className="h-4 w-40" />
        </div>
      ) : (
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-baseline gap-x-1.5">
            {struckPrice !== null && (
              <s className="text-lg text-muted-foreground/80 md:text-base">
                {formatPlanPrice(struckPrice, priceCurrency)}
              </s>
            )}
            <span className="text-[38px] font-bold leading-[1.4] tracking-tight text-primary-darker md:text-[28px] md:leading-[1.3]">
              {formatPlanPrice(monthly, priceCurrency)}
            </span>
            <span className="text-sm text-muted-foreground">/mo</span>
            {offer && (
              <OfferDiscountChip discountPercent={discount} className="ms-auto self-center" />
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {interval === 'annual' && annualTotal !== undefined
              ? `Billed yearly at ${formatPlanPrice(annualTotal, priceCurrency)}`
              : 'Billed monthly'}
          </p>
        </div>
      )}

      {isOwned ? (
        <Button
          type="button"
          disabled
          className="h-10 w-full rounded-[10px] border border-border bg-muted text-sm text-muted-foreground disabled:opacity-100 md:h-9"
        >
          {name === currentPlan ? 'Current plan' : 'Included in your plan'}
        </Button>
      ) : (
        <Button
          type="button"
          onClick={onSelect}
          disabled={pending || locked}
          aria-busy={pending}
          data-ph-selector={`${dataPhSelector}-plan-${name.toLowerCase()}-select`}
          className={cn(
            'h-10 w-full rounded-[10px] text-sm md:h-9',
            !recommended && 'border border-primary bg-white text-primary hover:bg-muted',
          )}
        >
          {pending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
          Upgrade to {name}
        </Button>
      )}

      <ul className="flex flex-col gap-[7px] border-t border-border pt-2.5 md:gap-1.5">
        {keyFeatures.map((feature) => (
          <li
            key={feature}
            className="flex items-center gap-2 text-xs text-primary-darker md:text-[13px]"
          >
            <Check className="size-[15px] shrink-0 text-primary" aria-hidden="true" />
            {feature}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function PlansModal({
  open,
  onOpenChange,
  plans,
  pricesLoading = false,
  currentPlan,
  currentPlanLabel,
  defaultInterval = 'annual',
  annualSavingsPercent,
  currency = 'USD',
  currencies,
  onCurrencyChange,
  offer,
  onTalkToFounder,
  onSelectPlan,
  pendingPlan,
  onCompareFeatures,
  dataPhSelector,
}: PlansModalProps) {
  const [interval, setBillingInterval] = React.useState<PlanBillingInterval>(defaultInterval);
  const [mobilePlan, setMobilePlan] = React.useState<UpgradePlanName | undefined>(undefined);
  // Only tick while the modal is showing.
  const countdown = useOfferCountdown(open ? offer : null);
  const activeOffer = offer && countdown.phase !== 'ended' ? offer : undefined;

  const shownOnMobile =
    mobilePlan ?? plans.find((plan) => plan.recommended)?.name ?? plans[0]?.name;
  // The currencies the cards are actually priced in; a price that couldn't be converted stays in
  // its fallback currency, so this can differ from the one picked.
  const pricedIn = pricesLoading
    ? [currency]
    : [...new Set(plans.map((plan) => plan.pricing[interval].currency ?? currency))];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideCloseBtn
        // Focus the dialog, not the close button, so it opens without a focus ring on the X.
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          (event.currentTarget as HTMLElement).focus();
        }}
        className="border-0 p-5 shadow-plans-modal outline-none sm:max-h-[92dvh] sm:max-w-240 sm:rounded-[18px] md:gap-4 md:p-6"
        data-ph-selector={dataPhSelector}
      >
        <div className="flex items-start gap-3">
          <div className="flex min-w-0 flex-1 flex-col sm:gap-2 md:items-center md:text-center">
            <DialogTitle className="pe-0 text-xl sm:text-2xl font-bold leading-[1.45] text-primary-darker md:text-2xl md:leading-[1.3]">
              Choose the plan <span className="max-sm:hidden">that&apos;s right for you</span>
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground md:text-base">
              Your current plan: {currentPlanLabel}
            </DialogDescription>
          </div>
          <DialogClose
            className="grid size-8 sm:size-10 shrink-0 cursor-pointer place-items-center rounded-[10px] border border-border text-muted-foreground transition-[color,border-color] duration-150 hover:border-primary-darker hover:text-primary-darker md:size-8"
            data-ph-selector={`${dataPhSelector}-close`}
          >
            <X className="size-[18px] md:size-4" aria-hidden="true" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </div>

        <div className="flex items-center justify-between gap-3 md:justify-center md:gap-5">
          <div
            role="radiogroup"
            aria-label="Billing interval"
            className="flex gap-1 rounded-[10px] border border-border bg-muted p-1"
          >
            {INTERVALS.map(({ value, label }) => {
              const selected = interval === value;
              return (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => setBillingInterval(value)}
                  data-ph-selector={`${dataPhSelector}-interval-${value}`}
                  className={cn(
                    'flex h-9 cursor-pointer items-center gap-2 rounded-lg border px-2.5 text-[13px] transition-[background-color,color] duration-150 md:px-4 md:text-sm',
                    selected
                      ? 'border-border bg-white font-bold text-primary'
                      : 'border-transparent text-muted-foreground hover:text-primary-darker',
                  )}
                >
                  {label}
                  {value === 'annual' && annualSavingsPercent !== undefined && (
                    <span className="rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700">
                      Save {annualSavingsPercent}%
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {onCurrencyChange && currencies && currencies.length > 1 ? (
            <CurrencyPicker
              currency={currency}
              currencies={currencies}
              onChange={onCurrencyChange}
              dataPhSelector={dataPhSelector}
            />
          ) : (
            <span className="shrink-0 text-sm text-muted-foreground">
              <CurrencyLabel currency={currency} />
            </span>
          )}
        </div>

        {offer && open && (
          <ExclusiveOfferBanner
            offer={offer}
            variant="modal"
            onClaim={() => onOpenChange(false)}
            onTalkToFounder={onTalkToFounder ?? (() => onOpenChange(false))}
            dataPhSelector={`${dataPhSelector}-offer`}
          />
        )}

        <div
          role="tablist"
          aria-label="Plan"
          className="flex rounded-[10px] border border-border bg-muted p-1 md:hidden"
        >
          {plans.map(({ name }) => {
            const selected = name === shownOnMobile;
            return (
              <button
                key={name}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => setMobilePlan(name)}
                data-ph-selector={`${dataPhSelector}-plan-${name.toLowerCase()}-tab`}
                className={cn(
                  'min-h-[42px] flex-1 cursor-pointer rounded-lg border px-1 py-2 text-sm transition-[background-color,color] duration-150',
                  selected
                    ? 'border-border bg-white font-bold text-primary'
                    : 'border-transparent text-muted-foreground',
                )}
              >
                {name}
              </button>
            );
          })}
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          {plans.map((plan) => (
            <PlanCard
              key={plan.name}
              plan={plan}
              interval={interval}
              currency={currency}
              pricesLoading={pricesLoading}
              offer={activeOffer}
              currentPlan={currentPlan}
              hiddenOnMobile={plan.name !== shownOnMobile}
              onSelect={() => onSelectPlan(plan.name, interval)}
              pending={pendingPlan === plan.name}
              locked={Boolean(pendingPlan) && pendingPlan !== plan.name}
              dataPhSelector={dataPhSelector}
            />
          ))}
        </div>

        <div className="flex flex-col items-center gap-2.5 text-center">
          <p className="max-w-[250px] text-[11px] text-muted-foreground md:max-w-none md:text-sm">
            Secure checkout · Cancel anytime · Prices in {pricedIn.join(' and ')}, excluding taxes
          </p>
          <button
            type="button"
            onClick={onCompareFeatures}
            data-ph-selector={`${dataPhSelector}-compare-features`}
            className="cursor-pointer text-[13px] font-medium text-primary hover:underline md:text-sm"
          >
            Compare all features
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
